import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Hash, ArrowRight, Loader2, Heart, Vote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";

export default function AudiencePortalPage() {
  const navigate = useNavigate();
  const [eventCode, setEventCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [festivalData, setFestivalData] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!eventCode.trim()) {
      toast.error("Please enter an Event Code");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/audience/portal/${eventCode.toUpperCase()}`);
      setFestivalData(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Event not found. Please check the code.");
      } else {
        toast.error("Something went wrong");
      }
      setFestivalData(null);
    } finally {
      setLoading(false);
    }
  };

  const votingComps = festivalData?.competitions?.filter(c => c.audience_voting_enabled) || [];
  const nonVotingComps = festivalData?.competitions?.filter(c => !c.audience_voting_enabled) || [];

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col items-center justify-start p-4 pt-12">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF007F]/10 rounded-2xl mb-4">
            <Heart className="w-8 h-8 text-[#FF007F]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
            Audience Voting
          </h1>
          <p className="text-[#A1A1AA]">Enter the event code to vote for your favorite dancers</p>
        </div>

        {/* Search Form */}
        <div className="glass p-6 rounded-2xl mb-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <Label className="text-white mb-2 block flex items-center gap-2">
                <Hash className="w-4 h-4 text-[#FF007F]" />
                Event Code
              </Label>
              <Input
                value={eventCode}
                onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                placeholder="e.g. ATH202625"
                className="h-14 text-xl text-center tracking-widest bg-black/30 border-white/10 text-white placeholder:text-[#52525B] font-mono"
                maxLength={12}
                data-testid="audience-event-code-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full btn-gold h-12 text-lg font-bold"
              disabled={loading || !eventCode.trim()}
              data-testid="audience-search-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Find Competitions
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Results */}
        {festivalData && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="text-center mb-4">
              <h2 className="text-white font-bold text-lg">{festivalData.festival_name}</h2>
              <p className="text-[#A1A1AA] text-sm">Code: {festivalData.event_code}</p>
            </div>

            {votingComps.length > 0 ? (
              <div className="space-y-3">
                {votingComps.map((comp) => (
                  <motion.div
                    key={comp.competition_id}
                    className="glass p-4 rounded-xl flex items-center justify-between"
                    whileTap={{ scale: 0.98 }}
                  >
                    <div>
                      <p className="text-white font-medium">{comp.category}</p>
                      <p className="text-[#FF007F] text-xs">
                        Your vote counts for {comp.audience_voting_percentage}%
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate(`/vote/${comp.competition_id}`)}
                      className="bg-[#FF007F]/20 text-[#FF007F] hover:bg-[#FF007F]/30"
                      size="sm"
                      data-testid={`vote-comp-${comp.competition_id}`}
                    >
                      <Vote className="w-4 h-4 mr-1" />
                      Vote
                    </Button>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="glass p-6 rounded-xl text-center">
                <Users className="w-10 h-10 text-[#27272a] mx-auto mb-3" />
                <p className="text-[#A1A1AA]">No competitions with voting enabled yet.</p>
                {nonVotingComps.length > 0 && (
                  <p className="text-[#52525B] text-sm mt-2">
                    {nonVotingComps.length} competition(s) found, but voting is not enabled.
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* Footer */}
      <div className="mt-auto pt-8 pb-4 text-center">
        <a href="/terms" className="text-[#52525B] text-xs hover:text-[#A1A1AA]">Terms of Use</a>
        <p className="text-[#27272a] text-xs mt-1">JackAndJillScore</p>
      </div>
    </div>
  );
}
